import { useState, useCallback, useRef } from 'react';
import { API_BASE, SHOP_DOMAIN, DEFAULT_SETTINGS } from '../constants';
import { useAuth } from '../auth';

export function useApi() {
  const { shopDomain, getSessionToken, ensureInstall } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // getSessionToken's identity can churn when App Bridge re-renders; keep it
  // in a ref so `request` stays stable and pages don't refetch spuriously.
  const sessionTokenRef = useRef(getSessionToken);
  sessionTokenRef.current = getSessionToken;
  // 同理保住 ensureInstall,并给它一个跨请求共享的在跑实例引用。
  const ensureInstallRef = useRef(ensureInstall);
  ensureInstallRef.current = ensureInstall;
  const installRetryRef = useRef(null);

  // 店铺标识的传递规则与后端一致:B 端接口一律不带任何店铺标识,后端从
  // session token(JWT)的 iss 声明里解析。因此所有管理端调用都是裸路径;
  // 不在 Shopify 管理页里打开(没有令牌)时,后端会拒绝并提示。

  const request = useCallback(async (endpoint, options = {}, retriedInstall = false) => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'Content-Type': 'application/json', ...options.headers };
      if (sessionTokenRef.current) {
        // Embedded mode: every admin API call carries the App Bridge session
        // token (freshly minted — App Bridge auto-refreshes expired ones).
        headers['Authorization'] = `Bearer ${await sessionTokenRef.current()}`;
      }
      const res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 店铺行不存在、或凭据失效被标记重装(卸载重装后旧令牌被 Shopify
        // 吊销)时,先跑一次安装流程(token exchange)再重试。
        if (data.error === 'shop is not installed' && !retriedInstall && ensureInstallRef.current) {
          if (!installRetryRef.current) {
            installRetryRef.current = ensureInstallRef.current();
          }
          try {
            await installRetryRef.current;
          } finally {
            installRetryRef.current = null;
          }
          return request(endpoint, options, true);
        }
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      return data;
    } catch (err) {
      if (err.name !== 'TypeError') setError(err.message);
      else setError('Cannot reach API server. Is the backend running?');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Explicit opt-in demo seed (Dashboard "Load demo plans" button). The
  // backend scopes it to the session's own shop; it never touches another
  // shop's data and only wipes plans with ?replace=true.
  const seedData = useCallback(async (replace = false) => (
    request(`/api/seed${replace ? '?replace=true' : ''}`, { method: 'POST' })
  ), [request]);

  const getPlans = useCallback(async () => (
    request('/api/plans')
  ), [request]);

  // Home-page numbers: last-30-days plan order count/revenue + 14-day series.
  const getStats = useCallback(async () => (
    request('/api/stats')
  ), [request]);

  // Whether the widget's app-embed block is enabled on the live theme.
  // enabled === null means unknown (not installed / no token / unreachable).
  // { refresh: true } skips the backend's short-lived result cache — used by
  // the window-focus re-check, which must see a theme-editor toggle made
  // seconds ago, not a cached answer.
  const getAppEmbedStatus = useCallback(async ({ refresh = false } = {}) => (
    request(`/api/app-embed${refresh ? '?refresh=1' : ''}`)
  ), [request]);

  // Store languages (published Shopify locales) for the plan editor's
  // language switcher. locales === null means unknown — the caller falls
  // back to the built-in preset language list.
  const getLocales = useCallback(async () => (
    request('/api/locales')
  ), [request]);

  const createPlan = useCallback(async (plan) => (
    request('/api/plans', {
      method: 'POST',
      body: JSON.stringify(plan)
    })
  ), [request]);

  const updatePlan = useCallback((id, plan) => (
    request(`/api/plans/${id}`, { method: 'PUT', body: JSON.stringify(plan) })
  ), [request]);

  const deletePlan = useCallback((id) => (
    request(`/api/plans/${id}`, { method: 'DELETE' })
  ), [request]);

  const getSettings = useCallback(async () => {
    const settings = await request('/api/settings');
    return { ...DEFAULT_SETTINGS, ...settings };
  }, [request]);

  // Partial settings update (PUT /api/settings is patch-shaped on the
  // backend): send only the changed keys, e.g. { adminLocale: 'zh-CN' }.
  const updateSettings = useCallback((patch) => (
    request('/api/settings', { method: 'PUT', body: JSON.stringify(patch) })
  ), [request]);

  const getThemePreset = useCallback((themeName) => (
    request(`/api/theme-anchors?themeName=${encodeURIComponent(themeName)}`)
  ), [request]);

  // Storefront (C-end) endpoint: the widget has no session token, so the
  // shop domain travels in the body (direct calls are unsigned by design).
  const calculateCart = useCallback((subtotal, selectedPlanIds) => (
    request('/api/cart/calculate', {
      method: 'POST',
      body: JSON.stringify({ shopDomain, subtotal, selectedPlanIds })
    })
  ), [request, shopDomain]);

  // Plan icon upload: one image file → POST /api/uploads (multipart) →
  // { url }. Content-Type must stay unset here — the browser fills in the
  // multipart boundary itself; only the session token rides along.
  const uploadFile = useCallback(async (file) => {
    const body = new FormData();
    body.append('file', file);
    const headers = {};
    if (sessionTokenRef.current) {
      headers['Authorization'] = `Bearer ${await sessionTokenRef.current()}`;
    }
    const res = await fetch(`${API_BASE}/api/uploads`, { method: 'POST', body, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
    return data.url;
  }, []);

  return {
    loading,
    error,
    getPlans,
    getStats,
    getAppEmbedStatus,
    getLocales,
    createPlan,
    updatePlan,
    deletePlan,
    seedData,
    getSettings,
    updateSettings,
    getThemePreset,
    calculateCart,
    uploadFile
  };
}
